import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Tippy from '@tippyjs/react/headless';
import { useNotificationStore } from '@/stores/notificationStore';
import {
  NOTIFICATION_TYPE_LABEL,
  NOTIFICATION_TYPE_ICONS,
  NOTIFICATION_TYPE_COLORS,
  getItemTargetUrl,
  formatNotificationTime,
} from '@/utils/notification';

interface NotificationPopoverProps {
  className?: string;
  /** 弹出位置方向，不传则由 Tippy 自动控制 */
  placement?: 'auto' | 'auto-start' | 'auto-end' | 'top' | 'top-start' | 'top-end' | 'bottom' | 'bottom-start' | 'bottom-end' | 'right' | 'right-start' | 'right-end' | 'left' | 'left-start' | 'left-end';
}

export function NotificationPopover({ className = '', placement }: NotificationPopoverProps) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const navigate = useNavigate();

  const {
    unreadCount,
    messages,
    loading,
    loadingMore,
    hasMore,
    fetchMessages,
    fetchMoreMessages,
    markAsRead,
    markAllAsRead,
  } = useNotificationStore();

  // 当展开面板或切换筛选 Filter 时拉取最新的消息数据
  useEffect(() => {
    if (open) {
      void fetchMessages({
        page: 1,
        page_size: 10,
        is_read: filter === 'unread' ? false : undefined,
      });
    }
  }, [open, filter, fetchMessages]);

  // 根据当前 Filter 过滤本地显示的消息列表
  const displayMessages = messages.filter((msg) =>
    filter === 'unread' ? !msg.is_read : true
  );

  // 监听列表滚动到达底部自动触底加载下一页
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 20) {
      if (!loading && !loadingMore && hasMore) {
        void fetchMoreMessages({
          is_read: filter === 'unread' ? false : undefined,
        });
      }
    }
  };

  const handleItemClick = (item: Api.Notification.MessageResponse) => {
    if (!item.is_read && item.id) {
      void markAsRead(item.id);
    }
    setOpen(false);
    const targetUrl = getItemTargetUrl(item);
    if (targetUrl && targetUrl !== '#') {
      navigate(targetUrl);
    }
  };

  const handleMarkAll = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await markAllAsRead();
  };

  return (
    <div className={`relative flex-shrink-0 ${className}`}>
      <Tippy
        interactive={true}
        visible={open}
        onClickOutside={() => setOpen(false)}
        placement={placement}
        offset={[0, 8]}
        appendTo={() => document.body}
        render={(attrs) => (
          <div
            {...attrs}
            className="w-80 sm:w-96 rounded-2xl border border-slate-200/80 bg-white/95 backdrop-blur-2xl shadow-[0_20px_50px_rgba(0,0,0,0.12),0_4px_10px_rgba(0,0,0,0.04)] z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-150 outline-none"
          >
            {/* Header 标头区 */}
          <div className="px-4 py-3 border-b border-slate-100/80 flex items-center justify-between bg-slate-50/50">
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm text-slate-800">站内消息</span>
              {unreadCount > 0 ? (
                <span className="px-2 py-0.5 rounded-full text-[11px] font-extrabold bg-red-100 text-red-600">
                  {unreadCount} 条未读
                </span>
              ) : (
                <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-600">
                  全部已读
                </span>
              )}
            </div>

            {unreadCount > 0 && (
              <button
                type="button"
                onClick={handleMarkAll}
                className="text-xs text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 px-2 py-1 rounded-lg font-medium transition-all flex items-center gap-1 cursor-pointer"
              >
                <i className="ri-check-double-line text-sm"></i>
                <span>一键已读</span>
              </button>
            )}
          </div>

          {/* Quick Filter Tabs 快捷筛选 */}
          <div className="flex items-center px-4 pt-2.5 pb-1 gap-2 border-b border-slate-100 text-xs font-medium text-slate-500">
            <button
              type="button"
              onClick={() => setFilter('all')}
              className={`px-3 py-1 rounded-full transition-all cursor-pointer ${
                filter === 'all'
                  ? 'bg-slate-900 text-white font-semibold shadow-2xs'
                  : 'hover:bg-slate-100 text-slate-600'
              }`}
            >
              全部
            </button>
            <button
              type="button"
              onClick={() => setFilter('unread')}
              className={`px-3 py-1 rounded-full transition-all cursor-pointer flex items-center gap-1 ${
                filter === 'unread'
                  ? 'bg-slate-900 text-white font-semibold shadow-2xs'
                  : 'hover:bg-slate-100 text-slate-600'
              }`}
            >
              <span>未读</span>
              {unreadCount > 0 && (
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    filter === 'unread' ? 'bg-red-400' : 'bg-red-500'
                  }`}
                ></span>
              )}
            </button>
          </div>

          {/* Content 列表区 */}
          <div
            onScroll={handleScroll}
            className="max-h-[360px] overflow-y-auto divide-y divide-slate-100/70 custom-scrollbar"
          >
            {loading && displayMessages.length === 0 ? (
              <div className="py-10 text-center">
                <div className="w-6 h-6 mx-auto mb-2 border-2 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
                <p className="text-slate-400 text-xs">加载消息中...</p>
              </div>
            ) : displayMessages.length === 0 ? (
              <div className="py-10 text-center">
                <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-slate-50 flex items-center justify-center text-slate-300">
                  <i className="ri-notification-off-line text-xl"></i>
                </div>
                <p className="text-xs text-slate-400 font-medium">
                  {filter === 'unread' ? '暂无未读消息' : '暂无相关消息通知'}
                </p>
              </div>
            ) : (
              <>
                {displayMessages.map((item) => {
                  const isRead = Boolean(item.is_read);
                  const msgType = item.message_type || 'SYSTEM';

                  return (
                    <div
                      key={item.id}
                      onClick={() => handleItemClick(item)}
                      className={`p-3.5 transition-all cursor-pointer flex gap-3 hover:bg-slate-50/80 ${
                        !isRead ? 'bg-emerald-50/20' : ''
                      }`}
                    >
                      {/* Icon 图标 */}
                      <div
                        className={`w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center text-sm ${
                          NOTIFICATION_TYPE_COLORS[msgType] || 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        <i
                          className={`${
                            NOTIFICATION_TYPE_ICONS[msgType] || 'ri-notification-3-line'
                          }`}
                        ></i>
                      </div>

                      {/* 文案区 */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                          <span
                            className={`text-xs truncate ${
                              !isRead ? 'font-bold text-slate-900' : 'font-medium text-slate-700'
                            }`}
                          >
                            {item.title}
                          </span>
                          {!isRead && (
                            <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0 shadow-2xs"></span>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-500 line-clamp-2 leading-relaxed mb-1">
                          {item.content}
                        </p>
                        <div className="flex items-center justify-between text-[10px] text-slate-400">
                          <span>{formatNotificationTime(item.created_at)}</span>
                          <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium scale-95 origin-right">
                            {NOTIFICATION_TYPE_LABEL[msgType] || msgType}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* 触底加载更多状态 */}
                {loadingMore && (
                  <div className="py-3 text-center flex items-center justify-center gap-2 bg-slate-50/40">
                    <div className="w-4 h-4 border-2 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
                    <span className="text-[11px] text-slate-400 font-medium">正在加载更多消息...</span>
                  </div>
                )}
                {!hasMore && displayMessages.length >= 5 && (
                  <div className="py-2.5 text-center text-[10px] text-slate-400 bg-slate-50/30">
                    已加载全部消息
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer 底部更多超链接 */}
          <div className="p-2 border-t border-slate-100 bg-slate-50/60">
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="w-full py-2 text-center text-xs font-semibold text-slate-700 hover:text-emerald-600 hover:bg-white rounded-xl transition-all flex items-center justify-center gap-1.5 border border-transparent hover:border-slate-200/60 shadow-2xs"
            >
              <span>查看全部消息通知</span>
              <i className="ri-arrow-right-s-line text-sm"></i>
            </Link>
          </div>
        </div>
      )}
    >
        {/* 消息通知 Trigger 触发按钮 */}
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          title={unreadCount > 0 ? `${unreadCount} 条未读消息` : '站内消息'}
          aria-expanded={open}
          className="w-8 h-8 lg:w-9 lg:h-9 xl:w-10 xl:h-10 flex items-center justify-center rounded-full text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-all cursor-pointer relative flex-shrink-0"
        >
          <i className="ri-notification-3-line text-lg lg:text-xl"></i>
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-extrabold flex items-center justify-center ring-2 ring-white shadow-xs animate-pulse">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </Tippy>
    </div>
  );
}

export default NotificationPopover;
